import React from "react";
import { toast } from "react-toastify";
import { getColorAnalysisData } from "../../app/api/colorAnalysis";
import {
  BASE_VALUE_FOR_COLOR_ANALYSIS_LEVEL1,
  COLOR_ANALYSIS_LEVELS,
  keysToRemoveForGraphAnalysis,
  LEVEL1_BACKGROUND_COLOR,
  mappedTrendFilterType,
  MIN_VALUE_FOR_COLOR_ANALYSIS_LEVEL1,
} from "../../constants/constants";
import { DataNode } from "../../interface/Trends";
import { User } from "../../types";
import {
  capitalizeFirstLetterAndRemainingSmallForEachWord,
  getKeyFromValueForTrendQueryType,
  invertColor,
  removeKeysFromObject,
} from "../commonUtils";
import {
  getTrendFiltersForGraphs,
  getTrendFiltersFromSession,
} from "./graphHelperUtils";
import { svgToBase64DataUrl } from "./svgDownloadUtils";
import {
  drawPdfFooter,
  drawPdfHeader,
  drawPdfSectionTitle,
  drawPdfTitle,
  loadPoppinsFonts,
  PDF_COLOR_ANALYSIS_TABLE_STYLES,
} from "./pdfEleganceUtils";
import * as d3 from "d3";
import jsPDF from "jspdf";
import "jspdf-autotable";
import store from "../../app/store";
import { setColorAnalysisL1, setL2Data } from "../../app/slices/trendSlice";

export const generateColorAnalysisS3JSON = async (user: User | null) => {
  try {
    const trendFilters = getTrendFiltersFromSession();
    if (!trendFilters || !trendFilters.color_analysis_level) return null;

    let colorForL2 = "";

    if (
      trendFilters?.colorAnalysis &&
      trendFilters?.color_analysis_level &&
      trendFilters?.color_analysis_level === "LEVEL2" &&
      trendFilters?.selected_color
    ) {
      colorForL2 = trendFilters?.selected_color;
    }
    removeKeysFromObject(trendFilters, keysToRemoveForGraphAnalysis);

    let l1Data = null;
    let l2Data = null;

    //l1 data
    const data = await setLevel1Data(user, trendFilters.query_type!);
    l1Data = data as DataNode;

    //l2 data
    if (colorForL2) {
      //l3 data
      l2Data = await getLevel2Data(user, colorForL2);
    }

    const filter = getKeyFromValueForTrendQueryType(trendFilters.query_type!);

    return {
      filterType: filter,
      l1Data,
      l2Data,
    };
  } catch (error) {
    console.error("Error while generating color analysis images", error);
    toast.error("Error while generating color analysis images", {
      hideProgressBar: true,
    });
    return null;
  }
};

export const showChart = (
  data: DataNode,
  chartRefCurrent: HTMLDivElement,
  level: COLOR_ANALYSIS_LEVELS,
  width: number,
  expandedView: boolean,
  setColor?: React.Dispatch<React.SetStateAction<string>>,
  snapshot: boolean = false
) => {
  showHueClusteredChart(
    data,
    chartRefCurrent,
    level,
    width,
    expandedView,
    setColor,
    snapshot
  );
};

type HueClusterNode = {
  data: DataNode;
  r: number;
  x: number;
  y: number;
};

type ColorFamilyBucket = {
  name: string;
  hue: number;
  isNeutral: boolean;
};

type PackLeaf = DataNode & {
  bucketName: string;
  hue: number;
  isNeutral: boolean;
};

type FamilyPackGroup = ColorFamilyBucket & {
  children: PackLeaf[];
};

const truncateColorLabel = (text: string, maxWidth: number) => {
  const context = document.createElement("canvas").getContext("2d");
  if (!context) {
    return text;
  }
  context.font = "8px Poppins";

  const fourLetters = text.slice(0, 4);
  if (context.measureText(fourLetters + "...").width <= maxWidth) {
    let truncated = text;
    while (context.measureText(truncated + "...").width > maxWidth) {
      truncated = truncated.slice(0, -1);
    }
    return truncated + "...";
  }

  const firstLetter = text[0];
  let dots = "...";
  while (dots.length > 0) {
    if (context.measureText(firstLetter + dots).width <= maxWidth) {
      return firstLetter + dots;
    }
    dots = dots.slice(0, -1);
  }

  return firstLetter;
};

const getCompressedMetricValue = (
  node: DataNode,
  level: COLOR_ANALYSIS_LEVELS
) => {
  if (level === "LEVEL1") {
    return (
      MIN_VALUE_FOR_COLOR_ANALYSIS_LEVEL1 +
      (Number(node.percentage || 0) / 100) *
        BASE_VALUE_FOR_COLOR_ANALYSIS_LEVEL1
    );
  }
  return Math.max(+(node.relativePercentage ?? node.percentage ?? 0), 0.001);
};

const getHslFromHex = (hex: string | undefined) => {
  const parsed = hex ? d3.color(hex) : null;
  if (!parsed) {
    return { h: 0, s: 0, l: 0.5 };
  }
  const hsl = d3.hsl(parsed);
  return {
    h: Number.isFinite(hsl.h) ? hsl.h : 0,
    s: Number.isFinite(hsl.s) ? hsl.s : 0,
    l: Number.isFinite(hsl.l) ? hsl.l : 0.5,
  };
};

const getColorfulness = (s: number, l: number) => {
  return s * (1 - Math.abs(2 * l - 1));
};

const FAMILY_NAME_BUCKETS: (ColorFamilyBucket & { pattern: RegExp })[] = [
  {
    pattern: /\b(teal|turquoise|aqua|cyan|seafoam|sea\s*green)\b/i,
    name: "blue",
    hue: 210,
    isNeutral: false,
  },
  {
    pattern:
      /\b(green|olive|moss|fern|sage|mint|emerald|lime|forest|pistachio|chartreuse|jade|kelly|hunter|avocado)\b/i,
    name: "green",
    hue: 110,
    isNeutral: false,
  },
  {
    pattern:
      /\b(blue|navy|cobalt|cerulean|denim|sky|indigo|azure|periwinkle)\b/i,
    name: "blue",
    hue: 210,
    isNeutral: false,
  },
  {
    pattern:
      /\b(red|burgundy|crimson|coral|pink|rose|magenta|salmon|cherry|wine|maroon|ruby|scarlet|blush|fuchsia|berry|raspberry)\b/i,
    name: "red",
    hue: 0,
    isNeutral: false,
  },
  {
    pattern:
      /\b(purple|violet|plum|lilac|grape|lavender|orchid|mauve|eggplant)\b/i,
    name: "purple",
    hue: 290,
    isNeutral: false,
  },
  {
    pattern: /\b(yellow|mustard|gold|lemon|canary|banana|ochre)\b/i,
    name: "yellow",
    hue: 52,
    isNeutral: false,
  },
  {
    pattern:
      /\b(orange|rust|copper|terracotta|peach|apricot|tangerine|brown|tan|khaki|espresso|chestnut|chocolate|camel|coffee|mocha|bronze|cognac)\b/i,
    name: "orange",
    hue: 30,
    isNeutral: false,
  },
  {
    pattern:
      /\b(black|white|grey|gray|greige|ivory|beige|nude|ecru|oatmeal|charcoal|cream|silver|ash|pearl|taupe|stone|sand|bone)\b/i,
    name: "neutral",
    hue: -1,
    isNeutral: true,
  },
];

const classifyByFamilyName = (
  familyName: string | undefined
): ColorFamilyBucket | null => {
  if (!familyName) return null;
  for (const bucket of FAMILY_NAME_BUCKETS) {
    if (bucket.pattern.test(familyName)) {
      return {
        name: bucket.name,
        hue: bucket.hue,
        isNeutral: bucket.isNeutral,
      };
    }
  }
  return null;
};

const isTrueNeutralHsl = (h: number, s: number, l: number) => {
  if (s < 0.08) return true;
  if (l < 0.12 && s < 0.2) return true;
  if (l > 0.93 && s < 0.22) return true;
  const hue = ((h % 360) + 360) % 360;
  if (hue >= 20 && hue <= 55 && s < 0.28 && l > 0.5 && l < 0.92) return true;
  return false;
};

const classifyByHex = (hex: string | undefined) => {
  const { h, s, l } = getHslFromHex(hex);
  if (isTrueNeutralHsl(h, s, l)) {
    return { name: "neutral", hue: -1, isNeutral: true };
  }

  const hue = ((h % 360) + 360) % 360;
  if (hue >= 330 || hue < 18) return { name: "red", hue: 0, isNeutral: false };
  if (hue < 45) return { name: "orange", hue: 30, isNeutral: false };
  if (hue < 62) return { name: "yellow", hue: 52, isNeutral: false };
  if (hue < 168) return { name: "green", hue: 110, isNeutral: false };
  if (hue < 255) return { name: "blue", hue: 210, isNeutral: false };
  return { name: "purple", hue: 290, isNeutral: false };
};

const pickMoreChromaticHex = (
  familyHex: string | undefined,
  displayHex: string | undefined
) => {
  const family = getHslFromHex(familyHex);
  const display = getHslFromHex(displayHex);
  const familyChroma = getColorfulness(family.s, family.l);
  const displayChroma = getColorfulness(display.s, display.l);
  return displayChroma >= familyChroma ? displayHex : familyHex;
};

const classifyColorFamily = (
  familyName?: string,
  familyHex?: string,
  displayHex?: string
) => {
  const fromName = classifyByFamilyName(familyName);
  if (fromName) return fromName;
  return classifyByHex(pickMoreChromaticHex(familyHex, displayHex));
};

const rotatePackedNodes = (
  nodes: HueClusterNode[],
  cx: number,
  cy: number,
  delta: number
) => {
  const cos = Math.cos(delta);
  const sin = Math.sin(delta);
  nodes.forEach(node => {
    const dx = node.x - cx;
    const dy = node.y - cy;
    node.x = cx + dx * cos - dy * sin;
    node.y = cy + dx * sin + dy * cos;
  });
};

const buildPackedFamilyNodes = (
  data: DataNode,
  level: COLOR_ANALYSIS_LEVELS,
  width: number
): {
  nodes: HueClusterNode[];
  enclosure: { x: number; y: number; r: number };
} | null => {
  const children =
    data.children?.filter(
      child =>
        child.name &&
        (child.percentage != null || child.relativePercentage != null)
    ) || [];
  if (children.length === 0) return null;

  const leaves: PackLeaf[] = children.map(child => {
    const bucket = classifyColorFamily(
      child.name,
      child.familyColor,
      child.color
    );
    return {
      name: child.name,
      color: child.color,
      familyColor: child.familyColor,
      percentage: child.percentage,
      relativePercentage: child.relativePercentage,
      pantone1: child.pantone1,
      pantone2: child.pantone2,
      value: getCompressedMetricValue(child, level),
      bucketName: bucket.name,
      hue: bucket.hue,
      isNeutral: bucket.isNeutral,
    };
  });

  const groups = new Map<string, FamilyPackGroup>();
  leaves.forEach(leaf => {
    const existing = groups.get(leaf.bucketName);
    if (existing) {
      existing.children.push(leaf);
      return;
    }
    groups.set(leaf.bucketName, {
      name: leaf.bucketName,
      hue: leaf.hue,
      isNeutral: leaf.isNeutral,
      children: [leaf],
    });
  });

  const familyGroups = Array.from(groups.values()).sort((a, b) => {
    if (a.isNeutral !== b.isNeutral) return a.isNeutral ? -1 : 1;
    return a.hue - b.hue;
  });
  familyGroups.forEach(group => {
    group.children.sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
  });

  const hierarchy = d3
    .hierarchy({
      name: "root",
      children: familyGroups,
    } as {
      name: string;
      value?: number;
      hue?: number;
      isNeutral?: boolean;
      children: FamilyPackGroup[];
    })
    .sum(d => d.value ?? 0)
    .sort((a, b) => {
      if (a.depth === 1 && b.depth === 1) {
        if (a.data.isNeutral !== b.data.isNeutral) {
          return a.data.isNeutral ? -1 : 1;
        }
        return (a.data.hue ?? 0) - (b.data.hue ?? 0);
      }
      return (b.value ?? 0) - (a.value ?? 0);
    });

  const packedRoot = d3
    .pack()
    .size([width, width])
    .padding(d => {
      if (d.depth === 0) return 3;
      if (d.depth === 1) return 2;
      return 1.5;
    })(hierarchy as unknown as d3.HierarchyNode<unknown>);

  const nodes: HueClusterNode[] = packedRoot.leaves().map(leaf => ({
    data: leaf.data as DataNode,
    r: leaf.r,
    x: leaf.x,
    y: leaf.y,
  }));

  const redFamily = (packedRoot.children || []).find(
    node => (node.data as FamilyPackGroup).name === "red"
  );
  if (redFamily) {
    const currentAngle = Math.atan2(
      redFamily.y - packedRoot.y,
      redFamily.x - packedRoot.x
    );
    rotatePackedNodes(
      nodes,
      packedRoot.x,
      packedRoot.y,
      Math.PI - currentAngle
    );
  }

  const enclosure = d3.packEnclose(
    nodes.map(node => ({ x: node.x, y: node.y, r: node.r }))
  ) || { x: packedRoot.x, y: packedRoot.y, r: packedRoot.r };

  return { nodes, enclosure };
};

const attachHueClusterTooltip = (
  container: d3.Selection<HTMLDivElement, unknown, null, undefined>,
  circle: SVGCircleElement,
  node: HueClusterNode,
  level: COLOR_ANALYSIS_LEVELS,
  expandedView: boolean
) => {
  const tooltipDiv = container
    .append("div")
    .attr("class", "custom-tooltip")
    .style("position", "absolute")
    .style("z-index", "1000")
    .style("background-color", "#f6f6f6")
    .style("border-radius", "5px")
    .style("padding", "5px")
    .style("border", "1px solid black")
    .style("text-align", "center")
    .style("font-size", expandedView ? "14px" : "12px")
    .style("pointer-events", "none")
    .html(
      `${node.data.name}<br>${
        level === "LEVEL1"
          ? node.data.percentage.toFixed(2)
          : node.data.relativePercentage?.toFixed(3) || "0.00"
      }%`
    );

  const circleRect = circle.getBoundingClientRect();
  const containerRect = container.node()?.getBoundingClientRect();
  if (containerRect && circleRect) {
    tooltipDiv
      .style(
        "left",
        `${circleRect.left - containerRect.left + circleRect.width / 2}px`
      )
      .style("top", `${circleRect.top - containerRect.top - 40}px`)
      .style("transform", "translateX(-50%)");
  }

  return tooltipDiv;
};

const showHueClusteredChart = (
  data: DataNode,
  chartRefCurrent: HTMLDivElement,
  level: COLOR_ANALYSIS_LEVELS,
  width: number,
  expandedView: boolean,
  setColor?: React.Dispatch<React.SetStateAction<string>>,
  snapshot: boolean = false
) => {
  const height = width;
  const packed = buildPackedFamilyNodes(data, level, width);
  if (!packed) return;
  const { nodes, enclosure } = packed;

  const cx = width / 2;
  const cy = height / 2;

  // The packed circle is drawn at the enclosure center with r = enclosure.r + 4.
  // For asymmetric data the enclosure center sits off the geometric center, so a
  // fixed box can crop the circle on one side (typically the right). Size the
  // viewBox from the circle's ACTUAL extents so the circle is never clipped at
  // the SVG edge, leaving breathing-room ("padding") around it on every side.
  const pad = 10;
  const drawnR = enclosure.r + 4;
  const vMinX = Math.min(enclosure.x - drawnR, 0) - pad;
  const vMinY = Math.min(enclosure.y - drawnR, 0) - pad;
  const vMaxX = Math.max(enclosure.x + drawnR, width) + pad;
  const vMaxY = Math.max(enclosure.y + drawnR, height) + pad;

  d3.select(chartRefCurrent).selectAll("*").remove();

  const container = d3
    .select(chartRefCurrent)
    .append("div")
    .style("position", "relative")
    .style("width", "100%")
    .style("height", "100%");

  const svg = container
    .append("svg")
    .attr("viewBox", `${vMinX} ${vMinY} ${vMaxX - vMinX} ${vMaxY - vMinY}`)
    .attr("width", width)
    .attr("height", height)
    .attr(
      "style",
      `max-width: 100%; height: auto; display: block; margin: 0 auto; cursor: pointer;background-color: ${!expandedView && snapshot ? "#f6f6f6" : "#ffffff"};`
    );

  const nodesBySize = nodes.slice().sort((a, b) => b.r - a.r);

  const enclosureFill =
    level === "LEVEL1"
      ? !expandedView && snapshot
        ? "#ffffff"
        : LEVEL1_BACKGROUND_COLOR
      : data.color || LEVEL1_BACKGROUND_COLOR;

  svg
    .append("circle")
    .attr("cx", enclosure.x)
    .attr("cy", enclosure.y)
    .attr("r", enclosure.r + 4)
    .attr("fill", enclosureFill)
    .attr("pointer-events", "none");

  svg
    .append("g")
    .selectAll("circle")
    .data(nodesBySize)
    .join("circle")
    .attr("cx", d => d.x ?? cx)
    .attr("cy", d => d.y ?? cy)
    .attr("r", d => d.r)
    .attr("fill", d => d.data.color || "#f6f6f6")
    .attr("fill-opacity", 1)
    .on("mouseover", function (_event, d) {
      d3.select(this).attr("stroke", invertColor(d.data.color));
      d3.select(this).attr("stroke-width", 1.5);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (this as any).customTooltip = attachHueClusterTooltip(
        container,
        this as SVGCircleElement,
        d,
        level,
        expandedView
      );
    })
    .on("mouseout", function () {
      d3.select(this).attr("stroke", null);
      d3.select(this).attr("stroke-width", null);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((this as any).customTooltip) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this as any).customTooltip.remove();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this as any).customTooltip = null;
      }
    })
    .on("mousemove", function () {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tooltip = (this as any).customTooltip;
      if (!tooltip) return;
      const circle = this as SVGCircleElement;
      const circleRect = circle.getBoundingClientRect();
      const containerRect = container.node()?.getBoundingClientRect();
      if (containerRect && circleRect) {
        tooltip
          .style(
            "left",
            `${circleRect.left - containerRect.left + circleRect.width / 2}px`
          )
          .style(
            "top",
            `${circleRect.top - containerRect.top - (expandedView ? 50 : 45)}px`
          )
          .style("transform", "translateX(-50%)");
      }
    })
    .on("click", function (_event, d) {
      if (snapshot) return;
      if (setColor) {
        setColor(d.data.color || "#000000");
      }
    });

  const minNameRadius = expandedView ? 18 : 15;
  const minPercentRadius = expandedView ? 11 : 9;
  const labelFontSize = (radius: number) => {
    const base =
      level === "LEVEL1" ? (expandedView ? 10 : 8) : expandedView ? 8 : 6;
    return `${Math.min(base, Math.max(6, radius * 0.26))}px`;
  };

  svg
    .append("g")
    .attr("pointer-events", "none")
    .attr("text-anchor", "middle")
    .selectAll("text")
    .data(nodesBySize.filter(node => node.r >= minPercentRadius))
    .join("text")
    .attr("x", d => d.x ?? cx)
    .attr("y", d => (d.y ?? cy) - (d.r >= minNameRadius ? 4 : 0))
    .style("font-size", d => labelFontSize(d.r))
    .style("font-family", "Poppins")
    .style("font-weight", "normal")
    .style("fill", d => invertColor(d.data.color))
    .each(function (d) {
      const element = d3.select(this);
      const percentText =
        level === "LEVEL1"
          ? d.data.percentage.toFixed(2) + "%"
          : d.data.relativePercentage?.toFixed(3) + "%";
      element.selectAll("tspan").remove();

      if (d.r >= minNameRadius) {
        const truncatedName = truncateColorLabel(
          d.data.name || "",
          d.r * 2 - (expandedView ? 18 : 10)
        );
        element
          .append("tspan")
          .attr("x", d.x ?? cx)
          .attr("dy", 0)
          .text(truncatedName);
        element
          .append("tspan")
          .attr("x", d.x ?? cx)
          .attr("dy", "1.15em")
          .text(percentText);
        return;
      }

      element
        .append("tspan")
        .attr("x", d.x ?? cx)
        .attr("dy", "0.35em")
        .text(percentText);
    });
};

export const setLevel1Data = async (
  user: User | null,
  filterType: string,
  setColorAnalysisData?: React.Dispatch<React.SetStateAction<DataNode | null>>
) => {
  const trendFilters = getTrendFiltersForGraphs();
  if (!trendFilters) return null;

  const state = store.getState();
  const existingColorAnalysis = state.trends.trendsData?.colorAnalysisDataL1;
  const existingReqId = state.trends.trendsData?.req_id;

  let data: DataNode;
  if (existingColorAnalysis && trendFilters.req_id === existingReqId) {
    data = existingColorAnalysis;
  } else {
    delete trendFilters.req_id;
    const response = await getColorAnalysisData(
      {
        ...trendFilters,
        query_type: mappedTrendFilterType[filterType],
      },
      user
    );
    data = response?.data;
    data.color = LEVEL1_BACKGROUND_COLOR;
    formatDataForColorAnalysisLevel1(data);

    store.dispatch(setColorAnalysisL1(data));
  }

  setColorAnalysisData && setColorAnalysisData(data);
  return data;
};

export const getLevel2Data = async (user: User | null, colorForL2: string) => {
  const trendFilters = getTrendFiltersForGraphs();
  if (!trendFilters) return null;

  const state = store.getState();
  const existingL2Data = state.trends.trendsData?.l2Data[colorForL2];
  const existingReqId = state.trends.trendsData?.req_id;

  let data: DataNode;
  if (existingL2Data && trendFilters.req_id === existingReqId) {
    data = existingL2Data;
  } else {
    delete trendFilters.req_id;
    const response = await getColorAnalysisData(
      {
        ...trendFilters,
        primary_colour: colorForL2,
      },
      user
    );
    data = response?.data;

    // Store the API response in Redux
    store.dispatch(setL2Data({ color: colorForL2, data }));
  }

  return data;
};

export const formatDataForColorAnalysisLevel1 = (data: DataNode) => {
  let sum = 0;
  data?.children?.forEach(item => {
    // Calculate the value for the parent item
    const itemValue = Number(
      (
        MIN_VALUE_FOR_COLOR_ANALYSIS_LEVEL1 +
        Number(item?.percentage / 100) * BASE_VALUE_FOR_COLOR_ANALYSIS_LEVEL1
      ).toFixed(3)
    );
    item.value = itemValue;
    sum += itemValue;

    if (!item.children) {
      item.children = [];
    }

    while (item.children.length <= 1) {
      item.children.push({
        name: item.name,
        color: item.color,
      } as DataNode);
    }

    item.children?.forEach(child => {
      const calculatedValue = Number(
        (itemValue / (item?.children?.length || 1)).toFixed(4)
      );
      child.value = calculatedValue;
    });
  });
  data.value = sum;
};

export const handleDownloadColorAnalysis = async (
  level1Data: DataNode | null,
  level2Data: DataNode | null,
  colorAnalysisFilterType: string,
  setDownloadLoading: React.Dispatch<React.SetStateAction<boolean>>
) => {
  try {
    setDownloadLoading(true);
    const svgElements = [
      document.querySelector("#LEVEL1 svg"),
      document.querySelector("#LEVEL2 svg"),
    ].filter((svg): svg is SVGSVGElement => svg !== null);

    if (svgElements.length === 0) {
      toast.error("No graph available to download");
      setDownloadLoading(false);
      return;
    }

    // Create PDF in A3 size
    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a3",
    });

    await loadPoppinsFonts(pdf);

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Calculate table width first
    const tableWidth = pageWidth * 0.8;
    const tableMargin = (pageWidth - tableWidth) / 2;

    // Use different width for graph and center it
    const chartWidth = pageWidth * 0.4;
    const chartXOffset = (pageWidth - chartWidth) / 2; // Center the chart

    let currentY = 122;
    let pageNumber = 1;

    drawPdfHeader(pdf, pageWidth);
    drawPdfTitle(pdf, "Color Analysis", pageWidth, 70, false);

    pdf.setFont("Poppins", "normal");
    pdf.setFontSize(11);
    pdf.setTextColor(77, 77, 77);
    pdf.text(`Filter Type: ${colorAnalysisFilterType}`, pageWidth / 2, 96, {
      align: "center",
    });

    const processSVGElement = (
      svgElement: SVGSVGElement,
      index: number
    ): Promise<void> => {
      return new Promise(resolve => {
        const clonedSvg = svgElement.cloneNode(true) as SVGSVGElement;

        // Inject Poppins font style
        const style = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "style"
        );
        style.textContent = `
    * {
      font-family: 'Poppins', sans-serif !important;
    }
  `;
        clonedSvg.insertBefore(style, clonedSvg.firstChild);
        const svgData = new XMLSerializer().serializeToString(clonedSvg);
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        const img = new Image();

        img.onload = () => {
          const scale = 4;
          canvas.width = img.width * scale;
          canvas.height = img.height * scale;
          context?.scale(scale, scale);
          context?.drawImage(img, 0, 0);

          const imgData = canvas.toDataURL("image/png");
          const imgHeight = (img.height / img.width) * chartWidth;

          const graphTitle = `Level ${index + 1} Analysis Graph`;
          drawPdfSectionTitle(pdf, graphTitle, pageWidth, currentY);
          currentY += 18;

          pdf.addImage(
            imgData,
            "PNG",
            chartXOffset,
            currentY,
            chartWidth,
            imgHeight
          );
          currentY += imgHeight + 40;

          currentY = addTableToPDF(index, currentY);

          drawPdfFooter(pdf, pageWidth, pageHeight, pageNumber);

          if (index < svgElements.length - 1) {
            pdf.addPage();
            pageNumber += 1;
            currentY = 70;
            drawPdfHeader(pdf, pageWidth);
          }

          if (index === svgElements.length - 1) {
            pdf.save("Color Analysis.pdf");
            setDownloadLoading(false);
          }
          resolve();
        };

        img.onerror = () => {
          toast.error("Error loading image from SVG data");
          setDownloadLoading(false);
          resolve();
        };

        img.src = svgToBase64DataUrl(svgData);
      });
    };

    const addTableToPDF = (index: number, currentY: number): number => {
      let data: DataNode | null;
      let title: string;
      let tableHead: string[][];

      switch (index) {
        case 0:
          data = level1Data;
          title = "Level 1 Analysis Table";
          tableHead = [
            ["Sr. No.", "Color Family", "Percentage", "Pantone 1", "Pantone 2"],
          ];
          break;
        case 1:
          data = level2Data;
          title = "Level 2 Analysis Table";
          tableHead = [
            [
              "Sr. No.",
              "Color Family",
              "Combining Color Family",
              "Percentage",
              "Relative Percentage",
            ],
          ];
          break;
        default:
          return currentY;
      }

      if (!data) return currentY;

      drawPdfSectionTitle(pdf, title, pageWidth, currentY + 20);

      const tableData = data.children?.map((row, ind) => {
        if (index === 1) {
          // Level 2: Color Family (from data.name), Combining Color Family (from row.name), Percentage, Relative Percentage
          return [
            ind + 1,
            capitalizeFirstLetterAndRemainingSmallForEachWord(data?.name || ""),
            capitalizeFirstLetterAndRemainingSmallForEachWord(row.name),
            row?.percentage?.toFixed(2) || "0.00",
            row?.relativePercentage?.toFixed(3) || "0.000",
          ];
        } else {
          // Level 1: Color Family, Percentage, Pantone 1, Pantone 2
          return [
            ind + 1,
            capitalizeFirstLetterAndRemainingSmallForEachWord(row.name),
            row?.percentage?.toFixed(2) || "0.00",
            row?.pantone1 || "-",
            row?.pantone2 || "-",
          ];
        }
      });

      // @ts-expect-error: autoTable may not be typed
      const { finalY } = pdf.autoTable({
        head: tableHead,
        body: tableData || [],
        startY: currentY + 40,
        ...PDF_COLOR_ANALYSIS_TABLE_STYLES,
        tableWidth: tableWidth,
        margin: { left: tableMargin, right: tableMargin },
      });

      return finalY || currentY;
    };

    for (let i = 0; i < svgElements.length; i++) {
      await processSVGElement(svgElements[i], i);
    }
  } catch (error) {
    toast.error("Error downloading PDF");
    console.error("Error downloading PDF", error);
    setDownloadLoading(false);
  }
};
